// src/admin/AdminDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_URL || "https://api2.newstyle23.topindev.cloud";

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("today");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showNewBookingForm, setShowNewBookingForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [raffleParticipants, setRaffleParticipants] = useState([]);
  const [raffleLoading, setRaffleLoading] = useState(false);
  const [raffleWinners, setRaffleWinners] = useState(null);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      let endpoint;

      if (activeTab === "today") {
        endpoint = `${API_BASE}/bookings/today`;
      } else if (activeTab === "date") {
        endpoint = `${API_BASE}/bookings?date=${selectedDate}`;
      } else if (activeTab === "raffle") {
        await loadRaffleParticipants();
        setLoading(false);
        return;
      } else {
        endpoint = `${API_BASE}/bookings/upcoming`;
      }

      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBookings(res.data || []);
    } catch (err) {
      toast.error("Error cargando citas");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedDate]);

  const loadRaffleParticipants = async () => {
    setRaffleLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API_BASE}/raffle/participants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRaffleParticipants(res.data.participants || []);
    } catch (err) {
      toast.error("Error cargando participantes");
      console.error(err);
    } finally {
      setRaffleLoading(false);
    }
  };

  const handleDraw = async () => {
    if (raffleParticipants.length < 2) {
      toast.error("❌ Se necesitan al menos 2 participantes");
      return;
    }

    if (!window.confirm("¿Estás seguro de hacer el sorteo? Se seleccionarán 2 ganadores aleatoriamente.")) {
      return;
    }

    setRaffleLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.post(`${API_BASE}/raffle/draw`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRaffleWinners(res.data.winners);
      toast.success("🎁 ¡Sorteo realizado!");
    } catch (err) {
      toast.error(err.response?.data?.error || "Error haciendo sorteo");
    } finally {
      setRaffleLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  return (
    <div style={{
      padding: "clamp(1rem, 5vw, 2rem)",
      maxWidth: "1400px",
      margin: "0 auto",
      minHeight: "100vh",
    }}>
      <Toaster position="top-right" />

      <h1 style={{
        color: "white",
        marginBottom: "0.5rem",
        fontSize: "clamp(1.5rem, 5vw, 2rem)",
        fontWeight: "700",
      }}>
        📅 Gestiona tus Citas
      </h1>
      <p style={{
        color: "#cbd5e1",
        marginBottom: "1.5rem",
        fontSize: "clamp(0.9rem, 3vw, 1rem)",
      }}>
        Control total de tu agenda
      </p>

      {/* TABS - RESPONSIVE */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: "0.5rem",
        marginBottom: "1.5rem",
      }}>
        <TabButton
          active={activeTab === "today"}
          onClick={() => setActiveTab("today")}
          label="📅 Hoy"
        />
        <TabButton
          active={activeTab === "date"}
          onClick={() => setActiveTab("date")}
          label="📆 Fecha"
        />
        <TabButton
          active={showNewBookingForm}
          onClick={() => {
            setShowNewBookingForm(!showNewBookingForm);
            setEditingBooking(null);
          }}
          label="➕ Nueva"
        />
        <TabButton
          active={activeTab === "raffle"}
          onClick={() => setActiveTab("raffle")}
          label="🎁 Sorteo"
        />
      </div>

      {/* DATE PICKER */}
      {activeTab === "date" && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            marginBottom: "1.5rem",
            padding: "1rem",
            background: "rgba(51,65,85,0.5)",
            borderRadius: "12px",
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            flexWrap: "wrap",
            fontSize: "clamp(0.85rem, 2vw, 1rem)",
          }}
        >
          <label style={{ color: "white", fontWeight: "600", whiteSpace: "nowrap" }}>Fecha:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              padding: "0.6rem",
              borderRadius: "8px",
              border: "1px solid rgba(148,163,184,0.5)",
              background: "rgba(15,23,42,0.8)",
              color: "white",
              fontSize: "1rem",
              minWidth: "150px",
            }}
          />
        </motion.div>
      )}

      {/* RAFFLE TAB */}
      {activeTab === "raffle" && (
        <RafflePanel
          participants={raffleParticipants}
          winners={raffleWinners}
          loading={raffleLoading}
          onDraw={handleDraw}
          onLoadParticipants={loadRaffleParticipants}
        />
      )}

      {/* NUEVA CITA MANUAL */}
      {showNewBookingForm && (
        <NewBookingForm
          editingBooking={editingBooking}
          onSuccess={() => {
            setShowNewBookingForm(false);
            setEditingBooking(null);
            loadBookings();
          }}
          onCancel={() => {
            setShowNewBookingForm(false);
            setEditingBooking(null);
          }}
        />
      )}

      {/* BOOKINGS LIST */}
      {activeTab !== "raffle" && (
        <>
          {loading ? (
            <LoadingSpinner />
          ) : bookings.length === 0 ? (
            <EmptyState />
          ) : (
            <BookingsList 
              bookings={bookings} 
              onUpdate={loadBookings}
              onEdit={(booking) => {
                setEditingBooking(booking);
                setShowNewBookingForm(true);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function RafflePanel({ participants, winners, loading, onDraw, onLoadParticipants }) {
  useEffect(() => {
    onLoadParticipants();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: "linear-gradient(135deg, rgba(30,41,59,0.8), rgba(15,23,42,0.8))",
        padding: "clamp(1rem, 4vw, 1.5rem)",
        borderRadius: "12px",
        border: "2px solid #fbbf24",
      }}
    >
      <h2 style={{ color: "#fbbf24", marginBottom: "1rem", fontSize: "1.5rem" }}>
        🎁 Sorteo de Perfumes10 - 2 de Enero
      </h2>

      {/* INFO */}
      <div style={{
        background: "rgba(34,197,94,0.1)",
        border: "1px solid rgba(34,197,94,0.3)",
        padding: "1rem",
        borderRadius: "8px",
        marginBottom: "1.5rem",
        color: "#86efac",
      }}>
        <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600" }}>
          📊 Total de participantes: <strong>{participants.length}</strong>
        </p>
        <p style={{ margin: "0", fontSize: "0.9rem" }}>
          Todos los clientes que reserven citas desde la web entran automáticamente en el sorteo.
        </p>
      </div>

      {/* PARTICIPANTES */}
      {!winners && (
        <>
          <h3 style={{ color: "white", marginBottom: "1rem" }}>Participantes:</h3>
          <div style={{
            maxHeight: "400px",
            overflowY: "auto",
            marginBottom: "1.5rem",
          }}>
            {loading ? (
              <p style={{ color: "#cbd5e1" }}>⏳ Cargando...</p>
            ) : participants.length === 0 ? (
              <p style={{ color: "#cbd5e1" }}>No hay participantes aún</p>
            ) : (
              <div style={{ display: "grid", gap: "0.8rem" }}>
                {participants.map((p, idx) => (
                  <div key={p.id} style={{
                    background: "rgba(51,65,85,0.5)",
                    padding: "0.8rem",
                    borderRadius: "8px",
                    border: "1px solid rgba(148,163,184,0.2)",
                  }}>
                    <p style={{ margin: "0 0 0.3rem 0", color: "white", fontWeight: "600" }}>
                      {idx + 1}. {p.customerName}
                    </p>
                    <p style={{ margin: "0", color: "#cbd5e1", fontSize: "0.85rem" }}>
                      📱 {p.customerPhone}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* BOTÓN SORTEO */}
          {participants.length >= 2 && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onDraw}
              disabled={loading}
              style={{
                width: "100%",
                padding: "1rem",
                borderRadius: "10px",
                border: "none",
                background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
                color: "#020617",
                fontWeight: "700",
                fontSize: "1rem",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? "⏳ Haciendo sorteo..." : "🎉 HACER SORTEO"}
            </motion.button>
          )}
        </>
      )}

      {/* GANADORES */}
      {winners && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            background: "linear-gradient(135deg, rgba(34,197,94,0.2), rgba(59,130,246,0.2))",
            border: "2px solid #34d399",
            padding: "2rem",
            borderRadius: "12px",
            textAlign: "center",
          }}
        >
          <h3 style={{ color: "#34d399", fontSize: "1.8rem", marginBottom: "1.5rem" }}>
            🏆 ¡GANADORES DEL SORTEO!
          </h3>

          {winners.map((winner, idx) => (
            <div key={winner.id} style={{
              background: "rgba(51,65,85,0.5)",
              padding: "1.5rem",
              borderRadius: "10px",
              marginBottom: "1rem",
              border: "2px solid #fbbf24",
            }}>
              <p style={{ color: "#fbbf24", fontSize: "1.3rem", fontWeight: "700", margin: "0 0 0.5rem 0" }}>
                🥇 GANADOR {idx + 1}
              </p>
              <p style={{ color: "white", fontSize: "1.2rem", fontWeight: "600", margin: "0 0 0.5rem 0" }}>
                {winner.name}
              </p>
              <p style={{ color: "#cbd5e1", margin: "0" }}>
                📱 {winner.phone}
              </p>
              {winner.email && (
                <p style={{ color: "#cbd5e1", margin: "0.3rem 0 0 0" }}>
                  📧 {winner.email}
                </p>
              )}
            </div>
          ))}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1.5rem",
              padding: "0.8rem 1.5rem",
              borderRadius: "8px",
              border: "none",
              background: "rgba(148,163,184,0.2)",
              color: "#cbd5e1",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            ↺ Volver al panel
          </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      style={{
        padding: "0.6rem 1rem",
        borderRadius: "10px",
        border: "none",
        background: active
          ? "linear-gradient(135deg, #fbbf24, #f59e0b)"
          : "rgba(51,65,85,0.5)",
        color: active ? "#020617" : "white",
        fontWeight: "600",
        cursor: "pointer",
        fontSize: "clamp(0.8rem, 2vw, 0.95rem)",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </motion.button>
  );
}

function NewBookingForm({ editingBooking, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    serviceId: "",
    date: new Date().toISOString().split('T')[0],
    time: "10:00",
    sendWhatsApp: true,
  });
  const [services, setServices] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API_BASE}/services`);
        setServices(res.data || []);
        if (res.data?.length > 0 && !editingBooking) {
          setFormData(prev => ({ ...prev, serviceId: String(res.data[0].id) }));
        }
      } catch (err) {
        toast.error("Error cargando servicios");
      }
    };

    if (editingBooking) {
      setFormData({
        customerName: editingBooking.customerName,
        customerPhone: editingBooking.customerPhone,
        customerEmail: editingBooking.customerEmail || "",
        serviceId: String(editingBooking.serviceId),
        date: editingBooking.date.split('T')[0],
        time: editingBooking.date.split('T')[1].slice(0, 5),
        sendWhatsApp: true,
      });
    }

    fetchServices();
  }, [editingBooking]);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!formData.date || !formData.serviceId) {
        setAvailableTimes([]);
        return;
      }

      setLoadingTimes(true);
      try {
        const res = await axios.get(
          `${API_BASE}/availability?date=${formData.date}&serviceId=${formData.serviceId}`
        );
        
        const available = res.data.available || [];
        setAvailableTimes(available);
        
        if (available.length > 0 && !editingBooking) {
          setFormData(prev => ({
            ...prev,
            time: available[0]
          }));
        }
      } catch (err) {
        console.error("Error cargando disponibilidad:", err);
        setAvailableTimes([]);
      } finally {
        setLoadingTimes(false);
      }
    };

    fetchAvailability();
  }, [formData.date, formData.serviceId, editingBooking]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customerName || !formData.customerPhone || !formData.serviceId || !formData.date || !formData.time) {
      toast.error("❌ Completa todos los campos obligatorios");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("adminToken");

      if (editingBooking) {
        await axios.put(
          `${API_BASE}/bookings/${editingBooking.id}`,
          {
            customerName: formData.customerName,
            customerPhone: formData.customerPhone,
            customerEmail: formData.customerEmail,
            serviceId: parseInt(formData.serviceId),
            date: `${formData.date}T${formData.time}`,
            sendWhatsApp: formData.sendWhatsApp,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("✅ Cita modificada correctamente");
      } else {
        await axios.post(
          `${API_BASE}/bookings`,
          {
            customerName: formData.customerName,
            customerPhone: formData.customerPhone,
            customerEmail: formData.customerEmail,
            serviceId: parseInt(formData.serviceId),
            date: `${formData.date}T${formData.time}`,
            manual: true,
            sendWhatsApp: formData.sendWhatsApp,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("✅ Cita creada correctamente");
      }

      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || "Error procesando cita");
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (dateStr) => {
    const days = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const date = new Date(dateStr + "T00:00:00");
    return days[date.getDay()];
  };

  const dayName = getDayName(formData.date);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        marginBottom: "1.5rem",
        padding: "clamp(1rem, 4vw, 1.5rem)",
        background: "linear-gradient(135deg, rgba(30,41,59,0.8), rgba(15,23,42,0.8))",
        borderRadius: "12px",
        border: "1px solid rgba(251,191,36,0.3)",
      }}
    >
      <h3 style={{
        color: "#fbbf24",
        marginBottom: "1rem",
        fontSize: "clamp(1.1rem, 3vw, 1.3rem)",
      }}>
        {editingBooking ? "✏️ Modificar cita" : "➕ Crear nueva cita"}
      </h3>

      <form onSubmit={handleSubmit}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "1rem",
          marginBottom: "1rem",
        }}>
          <FormField
            label="👤 Nombre *"
            type="text"
            value={formData.customerName}
            onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
            placeholder="Nombre"
            required
          />

          <FormField
            label="📱 Teléfono *"
            type="tel"
            value={formData.customerPhone}
            onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
            placeholder="600123456"
            required
          />

          <FormField
            label="📧 Email"
            type="email"
            value={formData.customerEmail}
            onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
            placeholder="correo@email.com"
          />
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "1rem",
          marginBottom: "1rem",
        }}>
          <div>
            <label style={{ color: "white", display: "block", marginBottom: "0.3rem", fontWeight: "600", fontSize: "0.85rem" }}>
              💇 Servicio *
            </label>
            <select
              value={formData.serviceId}
              onChange={(e) => setFormData({ ...formData, serviceId: e.target.value })}
              style={{
                width: "100%",
                padding: "0.6rem",
                borderRadius: "8px",
                border: "1px solid rgba(148,163,184,0.5)",
                background: "rgba(15,23,42,0.9)",
                color: "white",
                fontSize: "0.9rem",
              }}
              required
            >
              <option value="">-- Selecciona --</option>
              {services.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <FormField
            label="📅 Fecha *"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />

          <div>
            <label style={{ color: "white", display: "block", marginBottom: "0.3rem", fontWeight: "600", fontSize: "0.85rem" }}>
              ⏰ Hora * ({dayName})
            </label>
            <select
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              style={{
                width: "100%",
                padding: "0.6rem",
                borderRadius: "8px",
                border: "1px solid rgba(148,163,184,0.5)",
                background: "rgba(15,23,42,0.9)",
                color: "white",
                fontSize: "0.9rem",
              }}
              required
              disabled={loadingTimes || availableTimes.length === 0}
            >
              <option value="">-- Selecciona --</option>
              {availableTimes.map(time => (
                <option key={time} value={time}>{time} ✅</option>
              ))}
            </select>
            {availableTimes.length === 0 && !loadingTimes && (
              <p style={{ color: "#f87171", marginTop: "0.3rem", fontSize: "0.75rem" }}>
                ❌ No disponible
              </p>
            )}
          </div>
        </div>

        <div style={{
          marginBottom: "1rem",
          padding: "0.8rem",
          background: "rgba(34,197,94,0.1)",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          border: "1px solid rgba(34,197,94,0.3)",
        }}>
          <input
            type="checkbox"
            id="sendWhatsApp"
            checked={formData.sendWhatsApp}
            onChange={(e) => setFormData({ ...formData, sendWhatsApp: e.target.checked })}
            style={{ width: "18px", height: "18px", cursor: "pointer" }}
          />
          <label htmlFor="sendWhatsApp" style={{
            color: "white",
            fontWeight: "600",
            cursor: "pointer",
            margin: "0",
            fontSize: "0.9rem",
          }}>
            📱 Enviar WhatsApp
          </label>
        </div>

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={loading || availableTimes.length === 0}
            style={{
              padding: "0.7rem 1.2rem",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
              color: "#020617",
              fontWeight: "700",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "0.9rem",
              opacity: loading ? 0.6 : 1,
              flex: 1,
              minWidth: "120px",
            }}
          >
            {loading ? "Guardando..." : editingBooking ? "✏️ Guardar" : "✅ Crear"}
          </motion.button>

          {editingBooking && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={onCancel}
              style={{
                padding: "0.7rem 1.2rem",
                borderRadius: "10px",
                border: "1px solid rgba(239,68,68,0.5)",
                background: "transparent",
                color: "#f87171",
                fontWeight: "700",
                cursor: "pointer",
                fontSize: "0.9rem",
                flex: 1,
                minWidth: "120px",
              }}
            >
              ❌ Cancelar
            </motion.button>
          )}
        </div>
      </form>
    </motion.div>
  );
}

function FormField({ label, type, value, onChange, placeholder, required }) {
  return (
    <div>
      <label style={{ color: "white", display: "block", marginBottom: "0.3rem", fontWeight: "600", fontSize: "0.85rem" }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        style={{
          width: "100%",
          padding: "0.6rem",
          borderRadius: "8px",
          border: "1px solid rgba(148,163,184,0.5)",
          background: "rgba(15,23,42,0.9)",
          color: "white",
          fontSize: "0.9rem",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

function BookingsList({ bookings, onUpdate, onEdit }) {
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      {bookings.map((booking, idx) => (
        <BookingCard
          key={booking.id}
          booking={booking}
          index={idx}
          onUpdate={onUpdate}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
}

function BookingCard({ booking, index, onUpdate, onEdit }) {
  const date = new Date(booking.date);
  const dateText = date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const statusColors = {
    PENDING: { color: "#fbbf24", label: "⏳ PENDIENTE" },
    CONFIRMED: { color: "#34d399", label: "✅ CONFIRMADA" },
    CANCELLED: { color: "#f87171", label: "❌ CANCELADA" },
  };

  const status = statusColors[booking.status] || statusColors.PENDING;

  const handleConfirm = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(
        `${API_BASE}/bookings/${booking.id}/confirm`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("✅ Confirmada");
      onUpdate();
    } catch (err) {
      toast.error("Error confirmando");
    }
  };

  const handleCancel = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(
        `${API_BASE}/bookings/${booking.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("✅ Cancelada");
      onUpdate();
    } catch (err) {
      toast.error("Error cancelando");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      style={{
        background: "linear-gradient(135deg, rgba(30,41,59,0.8), rgba(15,23,42,0.8))",
        padding: "clamp(1rem, 4vw, 1.5rem)",
        borderRadius: "12px",
        border: `2px solid ${status.color}`,
      }}
    >
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1rem",
        gap: "1rem",
        flexWrap: "wrap",
      }}>
        <h3 style={{ color: "white", margin: "0", fontSize: "clamp(0.95rem, 3vw, 1.1rem)" }}>
          👤 {booking.customerName}
        </h3>
        <div style={{
          padding: "0.3rem 0.8rem",
          borderRadius: "6px",
          background: `rgba(${parseInt(status.color.slice(1,3), 16)}, ${parseInt(status.color.slice(3,5), 16)}, ${parseInt(status.color.slice(5,7), 16)}, 0.15)`,
          color: status.color,
          fontWeight: "700",
          fontSize: "0.75rem",
        }}>
          {status.label}
        </div>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: "0.8rem",
        marginBottom: "1rem",
        paddingBottom: "1rem",
        borderBottom: "1px solid rgba(148,163,184,0.2)",
        fontSize: "0.85rem",
      }}>
        <InfoBlock label="📅" value={dateText} />
        <InfoBlock label="💇" value={booking.service?.name || "N/A"} />
        <InfoBlock label="📱" value={booking.customerPhone} />
      </div>

      <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", fontSize: "0.8rem" }}>
        {booking.status === "PENDING" && (
          <ActionButton label="✅ Confirmar" color="#34d399" onClick={handleConfirm} />
        )}
        {booking.status !== "CANCELLED" && (
          <ActionButton label="❌ Cancelar" color="#f87171" onClick={handleCancel} />
        )}
        <ActionButton label="✏️ Editar" color="#8b5cf6" onClick={() => onEdit(booking)} />
      </div>
    </motion.div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div>
      <p style={{
        fontSize: "0.7rem",
        opacity: 0.6,
        margin: "0 0 0.2rem 0",
      }}>
        {label}
      </p>
      <p style={{
        fontSize: "0.85rem",
        fontWeight: "600",
        margin: "0",
        color: "white",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}>
        {value}
      </p>
    </div>
  );
}

function ActionButton({ label, color, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      type="button"
      style={{
        padding: "0.5rem 0.8rem",
        borderRadius: "8px",
        background: `rgba(${parseInt(color.slice(1,3), 16)}, ${parseInt(color.slice(3,5), 16)}, ${parseInt(color.slice(5,7), 16)}, 0.15)`,
        border: `1.5px solid ${color}`,
        color: color,
        fontWeight: "700",
        cursor: "pointer",
        fontSize: "0.75rem",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={(e) => {
        e.target.style.background = color;
        e.target.style.color = "#020617";
      }}
      onMouseLeave={(e) => {
        e.target.style.background = `rgba(${parseInt(color.slice(1,3), 16)}, ${parseInt(color.slice(3,5), 16)}, ${parseInt(color.slice(5,7), 16)}, 0.15)`;
        e.target.style.color = color;
      }}
    >
      {label}
    </motion.button>
  );
}

function LoadingSpinner() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: "center", padding: "2rem" }}>
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2 }} style={{ fontSize: "2rem", marginBottom: "1rem" }}>
        ⏳
      </motion.div>
      <p style={{ opacity: 0.7, color: "white" }}>Cargando...</p>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: "center", padding: "2rem", borderRadius: "12px", background: "rgba(251,191,36,0.05)", border: "2px dashed rgba(251,191,36,0.2)" }}>
      <p style={{ fontSize: "2rem", margin: "0 0 0.5rem 0" }}>😎</p>
      <p style={{ opacity: 0.7, margin: "0", color: "white" }}>No hay citas</p>
    </motion.div>
  );
}